import { call, delay, put, race, select, take } from 'redux-saga/effects';
import {
    cancelRun,
    createRun,
    deleteRun,
    getLimits,
    getRun,
    getRunResults,
    getRuns,
    getScenarios
} from '../simulation.backend';
import {
    createRunFail,
    fetchRuns,
    createRunSuccess,
    fetchRunFail,
    fetchRunResultsFail,
    fetchRunResultsSuccess,
    fetchRunRetry,
    fetchRunsFail,
    fetchRunsGaveUp,
    fetchRunsRetry,
    fetchRunsSuccess,
    fetchRunSuccess,
    fetchRunToCopyFail,
    fetchRunToCopySuccess,
    fetchScenariosFail,
    fetchScenariosSuccess,
    fetchSimulationLimits,
    fetchSimulationLimitsFail,
    fetchSimulationLimitsSuccess,
    runActionFail,
    runActionSuccess
} from './actions';
import {
    STOP_FOLLOWING_RUN,
    STOP_FOLLOWING_RUNS_LIST
} from './actionTypes';
import {
    makeErrorPayload,
    makeSuccessPayload
} from '../../../../../shared/helpers/payload.helper';
import {
    setErrorToast,
    setSuccessToast
} from '../../../../../shared/toast/redux/actions';
import { redirectTo } from '../../../../../routing/redirectService';
import {
    getPollDelay,
    isActiveStatus,
    isTerminalStatus,
    MAX_CONSECUTIVE_POLL_FAILURES,
    RESULTS_POLL_EVERY_N_TICKS,
    RUN_POLL_INTERVAL_MS,
    RUNS_LIST_POLL_INTERVAL_MS
} from '../../simulation.model';
import { getRunsListState } from '../selectors';
import { SimulationPath } from '../../simulation.paths';

export function* fetchRunsSaga(action) {
    try {
        const response = yield call(getRuns, action.payload);
        yield put(fetchRunsSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchRunsFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* fetchScenariosSaga() {
    try {
        const response = yield call(getScenarios);
        yield put(fetchScenariosSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchScenariosFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* fetchSimulationLimitsSaga() {
    try {
        const response = yield call(getLimits);
        yield put(fetchSimulationLimitsSuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchSimulationLimitsFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

export function* createRunSaga(action) {
    try {
        const response = yield call(createRun, action.payload);
        yield put(createRunSuccess(response.data));
        yield put(setSuccessToast(makeSuccessPayload('The run was queued.')));
        yield call(redirectTo, SimulationPath.run(response.data.id));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(createRunFail(formattedError));
        yield put(setErrorToast(formattedError));
        // A refusal for a limit or a kill switch is about what the limits are now, which the form shows
        yield put(fetchSimulationLimits());
    }
}

/* Reads the results of a run. A failed read is not shown: the next one is tried, and the run itself says what went wrong. */
function* fetchResults(runId) {
    try {
        const response = yield call(getRunResults, runId);
        yield put(fetchRunResultsSuccess(response.data));
    } catch (error) {
        yield put(fetchRunResultsFail(makeErrorPayload(error)));
    }
}

/*
  Reads a run every few seconds until it is in a terminal state, and its results on every few reads and once when it is.

  The backend restarts on every deploy, so a read that fails is not the end of the run: what was last read stays on the screen,
  which is told that it is trying again (no message for each read), and the wait grows with each failure. After several in a row
  it gives up, and says so once. A run that does not exist (404) is given up on at once. Nothing here makes a run "failed".
*/
export function* followRunSaga(action) {
    const { runId } = action.payload;
    let failures = 0;
    let tick = 0;

    while (true) {
        try {
            const response = yield call(getRun, runId);
            failures = 0;
            yield put(fetchRunSuccess(response.data));

            const terminal = isTerminalStatus(response.data.status);
            if (terminal || tick % RESULTS_POLL_EVERY_N_TICKS === 0) {
                yield call(fetchResults, runId);
            }
            if (terminal) {
                return;
            }
        } catch (error) {
            const formattedError = makeErrorPayload(error);
            failures += 1;

            if (
                formattedError.statusCode === 404 ||
                failures >= MAX_CONSECUTIVE_POLL_FAILURES
            ) {
                yield put(fetchRunFail(formattedError));
                return;
            }
            yield put(fetchRunRetry());
        }

        tick += 1;
        yield delay(getPollDelay(failures, RUN_POLL_INTERVAL_MS));
    }
}

/* Follows a run until the screen that shows it is left. */
export function* watchFollowRunSaga(action) {
    yield race({
        follow: call(followRunSaga, action),
        stop: take(STOP_FOLLOWING_RUN)
    });
}

/*
  Keeps the list of runs up to date, on the page it is on, while a run in it is queued or running. Like the follow of a run, it
  tells the screen that it is trying again instead of showing a message for each read that failed, and gives up after several.
*/
export function* followRunsListSaga() {
    let failures = 0;

    while (true) {
        yield delay(getPollDelay(failures, RUNS_LIST_POLL_INTERVAL_MS));
        const { data, pagination, sort } = yield select(getRunsListState);

        // Nothing to keep up to date, unless a read failed and is being tried again
        if (!failures && !data.some(run => isActiveStatus(run.status))) {
            continue;
        }
        try {
            const response = yield call(getRuns, {
                page: pagination.page,
                pageSize: pagination.pageSize,
                ...sort
            });
            failures = 0;
            yield put(fetchRunsSuccess(response.data));
        } catch (error) {
            failures += 1;

            if (failures >= MAX_CONSECUTIVE_POLL_FAILURES) {
                yield put(fetchRunsGaveUp(makeErrorPayload(error)));
                return;
            }
            yield put(fetchRunsRetry());
        }
    }
}

/* Follows the list until the screen that shows it is left. */
export function* watchFollowRunsListSaga() {
    yield race({
        follow: call(followRunsListSaga),
        stop: take(STOP_FOLLOWING_RUNS_LIST)
    });
}

/* Reads the run that a new run is made from. */
export function* fetchRunToCopySaga(action) {
    try {
        const response = yield call(getRun, action.payload.runId);
        yield put(fetchRunToCopySuccess(response.data));
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(fetchRunToCopyFail(formattedError));
        yield put(setErrorToast(formattedError));
    }
}

function* refreshRunsList() {
    const { pagination, sort } = yield select(getRunsListState);

    yield put(
        fetchRuns({
            page: pagination.page,
            pageSize: pagination.pageSize,
            ...sort
        })
    );
}

export function* cancelRunSaga(action) {
    const { runId } = action.payload;

    try {
        const response = yield call(cancelRun, runId);
        yield put(runActionSuccess({ runId }));
        yield put(fetchRunSuccess(response.data));
        yield put(
            setSuccessToast(makeSuccessPayload('The run was cancelled.'))
        );
        yield call(refreshRunsList);
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(runActionFail({ runId, error: formattedError }));
        yield put(setErrorToast(formattedError));
    }
}

export function* deleteRunSaga(action) {
    const { runId, redirect } = action.payload;

    try {
        yield call(deleteRun, runId);
        yield put(runActionSuccess({ runId }));
        yield put(
            setSuccessToast(
                makeSuccessPayload('The run and what it produced were deleted.')
            )
        );
        if (redirect) {
            yield call(redirectTo, SimulationPath.RUNS);
        } else {
            yield call(refreshRunsList);
        }
    } catch (error) {
        const formattedError = makeErrorPayload(error);

        yield put(runActionFail({ runId, error: formattedError }));
        yield put(setErrorToast(formattedError));
    }
}
